import { Card, Text, Group, Button, Stack, Divider, Code, Select, Collapse, TextInput, TagsInput, Avatar, FileButton, SimpleGrid } from '@mantine/core';
import { useClipboard, useToggle } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useAuth } from '@/contexts/auth.ctx.tsx';
import { useProfile } from '$/contexts/profile.ctx';
import { useEffect, useState } from 'react';
import type { Profile } from '@prisma/client';
type ProfilePartial = Partial<Profile>;
import type { Accessor } from '@prisma/client';
type AccessorPartial = Partial<Accessor>;
import TestLink from '@/componets/testlink';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import TestMetaProfileCard from './test.meta.profile.tsx';
import { DemoArea } from '@/componets/Preview/DemoArea/index.tsx';
import { DemoRoot } from '@/componets/Preview/DemoRoot/index.tsx';
import ProfilePreviewCard from '@/componets/profile.card.tsx';

interface ProfileCardProps {
    testIdChange?: (_: string | undefined) => void;
}

export default function MainCard({ testIdChange }: ProfileCardProps) {
    const clipboard = useClipboard({ timeout: 800 });
    const { accessor, injectHeader, accessorCan } = useAuth();
    const { testCheck, createProfile, updateProfile, profileOptionData, selectedProfile, setProfileByID, profileCount, daysSinceLastTest } = useProfile();
    // const [profileOpt, setProfileOpt] = useState<string[]>([]);
    const [testResultTxt, setTestResultTxt] = useState<string>('No result yet');
    const [selectedName, setSelectedName] = useState<string | null>(null);
    // const [selectedProfile, setSelectedProfile] = useState<Profile | Accessor | null>(null);
    // const [accessorSelected, setAccessorSelected] = useState(false);
    const [testUrl, setTestUrl] = useState<string | null>(null);
    const [showProfileForm, setShowProfileForm] = useState(false);
    const [createFlag, setCreateFlag] = useState(false);
    // Profile Edit States
    const [srcImg, setSrcImg] = useState<string>('');
    const [newUpload, setNewUpload] = useState<File | null>(null);
    const [name, setName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [phone, setPhone] = useState<string>('');
    const [website, setWebsite] = useState<string>('');
    const [areaServed, setAreaServed] = useState<string[]>([]);
    const [flavorText, setFlavorText] = useState<string>('');
    const [holdingLicenses, setHoldingLicenses] = useState<string[]>([]);
    const [showProfileCard, setShowProfileCard] = useState(true);
    const [showPreview, togglePreview] = useToggle([1, 2] as const);

    useEffect(() => {
        const fetchImage = async (pid: string) => {
            const req = new Request(`/api/${pid}/img`, {
                method: 'GET'
            });
            const res = await fetch(req);
            if (res.status === 200) {
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                setSrcImg(url);
            }
        }
        if (!!selectedProfile && !createFlag) {
            setTestUrl(`https://${location.host}/t/${selectedProfile.testAccessId}`);
            setName(selectedProfile.name);
            setEmail(selectedProfile.email || '');
            setPhone(selectedProfile.phone || '');
            setWebsite(selectedProfile.website || '');
            fetchImage(selectedProfile.testAccessId);
            setTestResultTxt(selectedProfile.testResult || 'No result yet');
            setAreaServed(JSON.parse(selectedProfile.areaServed || '[]'));
            setFlavorText(selectedProfile.flavorText || '');
            setHoldingLicenses(JSON.parse(selectedProfile.holdingLicenses || '[]'));
        } else {
            setTestUrl('Generated when saved');
            setName('');
            setEmail('');
            setPhone('');
            setWebsite('');
            setSrcImg('');
            setAreaServed([]);
            setFlavorText('');
            setHoldingLicenses([]);
        }
    }, [selectedProfile, accessor]);

    useEffect(() => {
        if (!!newUpload) {
            setSrcImg(URL.createObjectURL(newUpload));
            uploadImage()
                .then(() => {
                    notifications.show({
                        title: 'Image Uploaded',
                        message: 'Your image has been uploaded',
                        color: 'yenta-light-blue'
                    });
                })
                .catch((err) => {
                    console.error('uploadImage::error', err);
                    notifications.show({
                        title: 'Image Upload Failed',
                        message: 'Your image has not been uploaded',
                        color: 'red'
                    });
                });
        }
    }, [newUpload])

    // This common function is used to get the data block for the profile or accessor
    const getDataBlk = (bk: ProfilePartial | AccessorPartial) => {
        if (name !== selectedProfile?.name) {
            bk.name = name;
        }
        if (email !== selectedProfile?.email) {
            bk.email = email;
        }
        if (phone !== selectedProfile?.phone) {
            bk.phone = phone;
        }
        if (website !== selectedProfile?.website) {
            bk.website = website;
        }
        if (!createFlag) {
            bk.id = selectedProfile?.id;
        }
        if (!!selectedProfile && !('areaServed' in selectedProfile)) {
            return bk;
        }
        if (areaServed !== JSON.parse(selectedProfile?.areaServed || '[]')) {
            // @ts-ignore
            bk.areaServed = JSON.stringify(areaServed);
        }
        if (flavorText !== selectedProfile?.flavorText) {
            // @ts-ignore
            bk.flavorText = flavorText;
        }
        if (holdingLicenses !== JSON.parse(selectedProfile?.holdingLicenses || '[]')) {
            // @ts-ignore
            bk.holdingLicenses = JSON.stringify(holdingLicenses);
        }
        return bk;
    }


    const saveProfile = async () => {
        if (!accessor) {
            notifications.show({
                title: 'Profile Update Failed',
                message: 'You are not logged in',
                color: 'red'
            });
            return;
        }
        const sendData: ProfilePartial = getDataBlk({
            ownerId: accessor.id
        });
        console.log('save button pressed - sending data:', sendData);
        try {
            if (createFlag) {
                const newProfile = await createProfile(sendData);
                setCreateFlag(false);
                setProfileByID(newProfile.id);
                setTestUrl(`https://${location.host}/t/${newProfile.testAccessId}`);
                notifications.show({
                    title: 'Profile Created',
                    message: 'Your profile has been created',
                    color: 'yenta-light-blue'
                });
            } else {
                await updateProfile(sendData);
                notifications.show({
                    title: 'Profile Updated',
                    message: 'Your profile has been updated',
                    color: 'yenta-light-blue'
                });
            }

        } catch (error) {
            console.error('profile update failed', error);
            notifications.show({
                title: 'Profile Update Failed',
                message: 'Your profile has not been updated',
                color: 'red'
            });
        }
    }

    const profileFormCard = () => {
        return (<Card withBorder w={{ base: '100%', md: '95%' }} mx={'auto'} mt={'md'}>
            <Group justify='space-between' gap={'xl'} wrap='nowrap' mb={showProfileForm ? 'md' : '0'}>
                {/* This div is a shortcut for getting the text centered and the button to the right */}
                <Text size={'1.4rem'} fw={300}>Test Profile</Text>
                <Group justify='flex-end'>
                    {showProfileForm && <Button onClick={saveProfile} color='yellow'>{createFlag ? 'Create' : 'Save'}</Button>}
                    <Button onClick={() => setShowProfileForm(!showProfileForm)}>
                        {showProfileForm ? 'Close' : 'Open'}
                        {showProfileForm ? <IconChevronUp /> : <IconChevronDown />}
                    </Button>
                </Group>
            </Group>
            <Collapse in={showProfileForm}>
                <Divider />
                <Stack mt={'md'}>

                    <Group wrap={'nowrap'}>
                        <SimpleGrid cols={{ base: 2, md: 3 }} flex={3}>
                            <TextInput
                                label='Name'
                                description='The name for the profile also shown on the test page'
                                value={name} onChange={(e) => setName(e.target.value)}
                                placeholder='Name'
                            />
                            <TextInput
                                label='Email'
                                description='This email will be shown to users on the test page'
                                value={email} onChange={(e) => setEmail(e.target.value)}
                                placeholder='Email'
                            />
                            <TextInput
                                label='Phone'
                                description='This phone number will be shown to users on the test page'
                                value={phone} onChange={(e) => setPhone(e.target.value)}
                                placeholder='Phone'
                            />
                            <TextInput
                                label='Website'
                                description='This website will be shown to users on the test page, or hidden if left blank'
                                value={website} onChange={(e) => setWebsite(e.target.value)}
                                placeholder='https://example.com'
                            />
                            <TextInput
                                label='Flavor Text'
                                description='This text will be shown to users on the test page'
                                value={flavorText} onChange={(e) => setFlavorText(e.target.value)} />
                            <TagsInput
                                label='Holding Licenses'
                                description='This is the license that the profile holds, or hidden if left blank. Use ".", "|", or "/" to separate licenses.'
                                value={holdingLicenses}
                                onChange={(e) => setHoldingLicenses(e)}
                                splitChars={['.', '|', '/']}
                            />
                            <TagsInput
                                label='Area Served'
                                description='This is the area that the profile serves, or hidden if left blank. Use ".", "|", or "/" to separate areas.'
                                value={areaServed} onChange={(e) => setAreaServed(e)}
                                splitChars={['.', '|', '/']}
                            />
                        </SimpleGrid>
                        {createFlag ? <>
                            <Text>You must save the new profile before uploading an image</Text>
                        </> : <Stack justify='center' align='center'>
                            <Avatar src={srcImg} size={'100px'} />
                            <Group wrap='nowrap'>
                                <FileButton accept='image/*' onChange={setNewUpload}>
                                    {(props) => <Button {...props}>Upload image</Button>}
                                </FileButton>
                                <Button variant='light' disabled={!srcImg || !newUpload}>Delete</Button>
                            </Group>
                        </Stack>}
                    </Group>

                </Stack>
                {!createFlag && <Stack justify='space-evenly' align='center'>
                    <Divider
                        onClick={() => togglePreview()}
                        style={{ cursor: 'pointer' }}
                        label={<Group>
                            {showPreview === 1 ? <IconChevronDown /> : <IconChevronUp />}
                            <Text size='1.1rem'>Preview</Text>
                            {showPreview === 1 ? <IconChevronDown /> : <IconChevronUp />}
                        </Group>} my="md" w="100%" />
                    <Collapse in={showPreview === 1} w='100%'>
                        <DemoRoot style={{ width: '100%' }}>
                            <DemoArea
                                withPadding
                                centered
                                dimmed
                                maxWidth={600}
                                minHeight={200}
                            >
                                <ProfilePreviewCard
                                    profileImg={srcImg}
                                    profileName={name}
                                    profilePersonality={testResultTxt}
                                    profileAbout={flavorText}
                                    profileContact={{ email, phone }}
                                    servicing={areaServed}
                                    profileCompany={name}
                                    companyLink={website}
                                />
                            </DemoArea></DemoRoot>
                    </Collapse>
                </Stack>}
            </Collapse>
        </Card>)
    }

    const uploadImage = async () => {
        if (!newUpload) {
            notifications.show({
                title: 'Upload Failed',
                message: 'No image selected',
                color: 'red'
            });
            return;
        } else if (!accessor || !selectedProfile) {
            notifications.show({
                title: 'Upload Failed',
                message: 'Are you sure you are logged in?',
                color: 'red'
            });
            return;
        }
        const formData = new FormData();
        formData.append('file', newUpload);
        const req = new Request(`/api/auth/${selectedProfile.testAccessId}/img`, {
            method: 'POST',
            body: formData
        });
        injectHeader(req);
        const res = await fetch(req);
        const data = await res.text();
        if (res.status === 200) {
            console.log('uploadImage::success');
            return;
        }
        console.log('uploadImage::res', data);
    }

    return (
        <Card my={'sm'} withBorder>
            <Group justify='space-between' wrap='nowrap'>
                <Select
                    placeholder='Select profile'
                    description='Test Profile'
                    w='auto'
                    data={[...profileOptionData, { value: 'creation', label: 'Create New Profile', disabled: profileCount >= (accessor?.profileLimit || 1) }]}
                    value={selectedProfile ? selectedProfile.id : 'creation'}
                    onChange={(value) => {
                        if (value === 'creation') {
                            setCreateFlag(true);
                            setProfileByID(null);
                            setShowProfileForm(true);
                            setTestResultTxt('No result yet');
                        } else if (typeof value === 'string') {
                            setCreateFlag(false);
                            setShowProfileForm(false);
                            setProfileByID(value);
                            testIdChange?.(selectedProfile?.testAccessId);
                        }
                    }}
                    onClick={e => e.stopPropagation()}
                    disabled={accessor?.profileLimit === 1}
                />
                <Text c='dimmed' size='sm' ta={'center'} onClick={() => setShowProfileCard(!showProfileCard)} style={{ cursor: 'pointer' }}>click to {showProfileCard ? 'hide' : 'show'}</Text>
                <Text c='dimmed' size='0.8rem' >Profiles Limit:<Text c="white" component='span' size='1.3rem' ml={'md'}>{profileCount}/{accessor?.profileLimit || 1}</Text></Text>
            </Group>
            <Collapse in={showProfileCard}>
                <Divider my={'md'} />
                {selectedProfile && !createFlag && selectedName !== 'Team Profile' && <Stack>
                    {/* Conditional for when a profile is selected and has a test result */}
                    {selectedProfile?.mostRecentTest ? <TestLink testUrl={testUrl!} /> : <Stack align='center'>
                        <Text fw={400} size='lg'>There is no test result for your account</Text>
                        <Text c='dimmed' size='md'>Once, completed you will be able to get your testing link</Text>
                        <Group>
                            <Button
                                component='a'
                                href={`${testUrl}?foraccessor=true`}
                                target='_blank'
                            >Take the Test</Button>
                            {/* TODO: make the button check for the accessors result -- probably a hook function, so that it can be used when the user updates the profile */}
                            <Button variant={'light'} onClick={() => testCheck(selectedProfile.id)}>Check</Button>
                        </Group>
                    </Stack>}
                    <Group justify='center'>
                        <Text>Your Personality</Text>
                        <Text fw={700} size='lg'>{testResultTxt}</Text>
                        <Divider mx={'md'} orientation='vertical' />
                        <Text>Test Taken</Text>
                        <Text fw={700} size='lg'>{daysSinceLastTest} Days ago</Text>
                    </Group>
                </Stack>}
                {selectedName === 'Team Profile' && <TestLink testUrl={testUrl!} mb={16} />}
                {(selectedProfile || createFlag) && profileFormCard()}
                {(selectedProfile || createFlag) && <TestMetaProfileCard />}
                {!(selectedProfile || createFlag) && <Stack>
                    <Text>Select a profile to view their information</Text>
                </Stack>}
            </Collapse>

        </Card>
    )
}